import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Stack,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { dateFormatter } from "../../utils/dateFormatter";
import { EstadoChip } from "../../utils/EstadoChip";
import { formatNumber } from "../../utils/formatters";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Tooltip } from "@mui/material";

const ModalDetalleProjects = ({ open, handleClose, project }) => {
  const baseUrl =
    process.env.REACT_APP_BACKEND_URL?.replace(/\/api$/, "") || "";
  const theme = useTheme();
  const [value, setValue] = useState(0);

  const auth_user_id = Number(localStorage.getItem("id"));
  const allowedUserIds = [5, 6, 13];
  const canSeePrice = allowedUserIds.includes(auth_user_id);

  if (!project) return null;

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const getFileConfig = (url) => {
    if (!url) return {};
    const lowerUrl = url.toLowerCase();

    if (lowerUrl.endsWith(".pdf")) {
      return {
        icon: <PictureAsPdfIcon sx={{ color: "#d32f2f", fontSize: 18 }} />,
        action: () => window.open(url, "_blank"),
      };
    }

    if (lowerUrl.endsWith(".doc") || lowerUrl.endsWith(".docx")) {
      return {
        icon: <DescriptionIcon sx={{ color: "#1976d2", fontSize: 18 }} />,
        action: () => downloadFile(url),
      };
    }

    if (lowerUrl.endsWith(".xls") || lowerUrl.endsWith(".xlsx")) {
      return {
        icon: <InsertDriveFileIcon sx={{ color: "#2e7d32", fontSize: 18 }} />,
        action: () => downloadFile(url),
      };
    }

    if (lowerUrl.endsWith(".ppt") || lowerUrl.endsWith(".pptx")) {
      return {
        icon: <InsertDriveFileIcon sx={{ color: "#ed6c02", fontSize: 18 }} />,
        action: () => downloadFile(url),
      };
    }

    return {
      icon: <DescriptionIcon sx={{ fontSize: 18 }} />,
      action: () => downloadFile(url),
    };
  };

  const downloadFile = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const renderDocumentVersions = (type, label) => {
    const doc = project.documents?.find((d) => d.type === type);

    if (!doc) {
      return { label, value: "-" };
    }

    return {
      label,
      value: (
        <Stack spacing={1}>
          <Typography fontSize="0.85rem">
            {project[`${type.toLowerCase()}_status`] || "-"}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {doc.versions.map((v, i) => {
              const url = `${baseUrl}/storage/${v.file_path}`;
              const { icon } = getFileConfig(url);
              return (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      cursor: "pointer",
                      px: 1,
                      py: 0.3,
                      borderRadius: 2,
                      backgroundColor: theme.palette.grey[100],
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    {icon}

                    <Tooltip
                      title={`Subido: ${dateFormatter(v.created_at)}`}
                      arrow
                    >
                      <Typography
                        fontSize="0.75rem"
                        fontWeight={600}
                        color="text.primary"
                      >
                        Versión {v.version}
                      </Typography>
                    </Tooltip>
                  </Stack>
                </a>
              );
            })}
          </Stack>
        </Stack>
      ),
    };
  };

  const rows = [
    { label: "Nr", value: project.id },
    { label: "Brand", value: project?.brand?.name || "Sin marca" },

    {
      label: "Main contact supervisor",
      value: project.main_contact_supervisor,
    },

    { label: "Model family", value: project.model_family },

    { label: "Models", value: project.models },

    {
      label: "Estimated volume",
      value: formatNumber(project.estimated_volume),
    },

    { label: "Plant line", value: project.plant_line },

    renderDocumentVersions("QUESTIONNAIRE", "Questionnaire completion"),

    renderDocumentVersions("NDA", "Nda status"),

    renderDocumentVersions("MOU", "Mou"),

    renderDocumentVersions("TCA", "Tca"),

    {
      label: "Trademark license agreement",
      value: project.trademark_license_agreement,
    },

    renderDocumentVersions("CONTRACT", "Equipment contract"),
    renderDocumentVersions("CONTRACT2", "Production contract"),

    renderDocumentVersions("BOM", "Bom"),

    {
      label: "Project status",
      value: project.project_status,
    },

    {
      label: "Assembly approach",
      value: project.assembly_approach,
    },

    {
      label: "Assembly line",
      value: project.assembly_line,
    },

    {
      label: "Homologation status",
      value: project.homologation_status,
    },

    {
      label: "Estimated sop",
      value: project.estimated_sop,
    },

    {
      label: "Project mgr",
      value: project.project_mgr,
    },

    {
      label: "Potential volume",
      value: formatNumber(project.potential_volume),
    },

    {
      label: "Comments",
      value: (
        <Stack spacing={0.5}>
          {project.months_comments?.length > 0 ? (
            project.months_comments.map((item) => (
              <Typography key={item.id} fontSize="0.85rem">
                <strong>{item.months}</strong> = {item.comment}
              </Typography>
            ))
          ) : (
            <Typography fontSize="0.85rem">Sin datos</Typography>
          )}
        </Stack>
      ),
    },

    {
      label: "Next steps",
      value: project.next_steps,
    },

    {
      label: "Pending points legal",
      value: project.pending_points_legal,
    },

    {
      label: "Production",
      value: (
        <Stack spacing={0.5}>
          {project.yearly_estimations?.length > 0 ? (
            [...project.yearly_estimations]
              .sort((a, b) => a.year - b.year)
              .map((item) => (
                <Typography key={item.id} fontSize="0.85rem">
                  <strong>{item.year}</strong> ={" "}
                  {formatNumber(Number(item.amount))}
                </Typography>
              ))
          ) : (
            <Typography fontSize="0.85rem">Sin datos</Typography>
          )}
        </Stack>
      ),
    },

    {
      label: "Support requested",
      value: project.support_requested,
    },
  ];

  // if (canSeePrice) {
  //   rows.push(renderDocumentVersions("PRICE", "Price agreement"));
  // }

  rows.push(
    renderDocumentVersions("LAYOUT", "Layout"),
    {
      label: "Created at",
      value: dateFormatter(project.created_at),
    },
    {
      label: "Status",
      value: <EstadoChip estado={project.estado} />,
    },
  );
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{ borderBottom: `1px solid ${theme.palette.divider}`, py: 2 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={700}>
            Project details
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box
        sx={{ borderBottom: 1, borderColor: "divider", px: { xs: 1, sm: 3 } }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          }}
        >
          <Tab label="Information" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 4 }}>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: "35%",
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {row.label}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {row.value || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default ModalDetalleProjects;
